import { Dispatch, SetStateAction } from 'react';
import { EntityList } from '../../../../entity-list/EntityList';
import { QueryResult } from '../../../../request/hooks/useQuery';
import { BackendSubscriptionAction } from '../../../../request/interfaces/backend-subscription';
import { JobObject, JobObjectListWithCount } from '../../../interfaces/job';
import { JobsAPI } from '../../../JobsAPI';
import {
  ListChanges,
  ListMetaData,
  ListMetaNetworkParams
} from '../../../../list-conductor/list-meta-data';
import { JobObjectsTable } from './JobObjectsTable';
import { OffsetPaginationData } from '../../../../list-conductor/offset-pagination-strategy';
import { JobObjectListGlobalContext } from './interface';
import { EntityListEntities } from '../../../../shared/interfaces/entity-list';
import { Filters } from '../../../../filter/Filters';
import { JOB_OBJECTS_LIST_FILTER } from './constants';

export interface JobObjectListProps {
  jobId: string;
  pipelineId: number;
  dataId: string;
  tableComponentProps: {
    rowsExpandedState: JobObjectListGlobalContext['rowsExpandedState'];
    setRowsExpandedState: Dispatch<SetStateAction<JobObjectListGlobalContext['rowsExpandedState']>>;
  };
  defaultListMeta?: ListMetaData<OffsetPaginationData>;
  isLoadOnly: boolean;
  latencyNotApplicable: boolean;
  onListMetaChange: (changes: ListChanges<OffsetPaginationData>) => void;
}

export function JobObjectList({
  jobId,
  pipelineId,
  dataId,
  tableComponentProps,
  isLoadOnly,
  latencyNotApplicable,
  defaultListMeta,
  onListMetaChange
}: JobObjectListProps) {
  const fetch = (params?: ListMetaNetworkParams): Promise<QueryResult<Array<JobObject>>> =>
    JobsAPI.getJobObjects(jobId, params).then((res: JobObjectListWithCount) => ({
      responseMetaData: {
        count: res.total
      },
      items: res.data
    }));

  const refetch = (currentJobObjects?: JobObject[]): Promise<JobObject[]> => {
    const objectIds = (currentJobObjects || []).map(jobObject => jobObject.objectId);

    if (!objectIds.length) {
      return Promise.resolve([]);
    }

    return JobsAPI.getJobObjects(jobId, {
      filters: [
        {
          fields: ['catalog_object_id'],
          operator: 'IN',
          values: objectIds
        }
      ]
    }).then(res =>
      res.data.sort((a, b) => {
        return objectIds.indexOf(a.objectId) - objectIds.indexOf(b.objectId);
      })
    );
  };

  return (
    <EntityList
      entity={EntityListEntities.JOB_OBJECTS}
      mode='standalone'
      classes={{ className: 'p-0 mt-5', toolbar: 'mt-1' }}
      dataId={dataId}
      dataSource$={fetch}
      pollDataSource$={refetch}
      listIcon='jobs'
      entityTitle='Objects'
      emptyTitle='No Object Found'
      emptySubTitle='Modify search term to find the desired Object'
      showSearch
      searchKey='object_name'
      searchPlaceholder='Search Objects'
      defaultListMeta={defaultListMeta}
      onListMetaChange={onListMetaChange}
      FiltersComponent={Filters}
      showFilters
      shimmerColumnsCount={7}
      newListConnectedTitle='No Object ingested'
      genericErrorTitle='Error while fetching Objects'
      genericErrorSubTitle='Retry Fetching the Objects'
      hideRefresh
      compact
      ListComponent={JobObjectsTable}
      listComponentProps={{
        ...tableComponentProps,
        jobId,
        isLoadOnly,
        latencyNotApplicable
      }}
      filtersComponentProps={{ filters: JOB_OBJECTS_LIST_FILTER, entity: 'jobs-object-list' }}
      subscriptionQueries={[
        {
          event: 'PIPELINE_STATUS_UPDATED',
          ids: [pipelineId],
          action: BackendSubscriptionAction.UPDATE
        }
      ]}
    />
  );
}
